import { useMemo } from "react";

import { OVERLAY_COLORS } from "./edgepan.constants";
import { DeadZoneSize, EdgeDeadZones } from "./edgepan.types";

interface EdgePanOverlayProps {
  ringWidthPercent: number;
  deadZones: EdgeDeadZones;
}

function formatDeadZone(size: DeadZoneSize): string {
  return size.unit === "px" ? `${size.value}px` : `${size.value * 100}%`;
}

export function EdgePanOverlay({
  ringWidthPercent,
  deadZones,
}: EdgePanOverlayProps) {
  const ringPercent = `${ringWidthPercent * 100}%`;

  const styles = useMemo(() => {
    const topDead = formatDeadZone(deadZones.top);
    const bottomDead = formatDeadZone(deadZones.bottom);
    const leftDead = formatDeadZone(deadZones.left);
    const rightDead = formatDeadZone(deadZones.right);

    return {
      container: {
        position: "absolute" as const,
        top: topDead,
        left: leftDead,
        right: rightDead,
        bottom: bottomDead,
        pointerEvents: "none" as const,
        zIndex: 1000,
      },
      topEdge: {
        position: "absolute" as const,
        top: 0,
        left: ringPercent,
        right: ringPercent,
        height: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      bottomEdge: {
        position: "absolute" as const,
        bottom: 0,
        left: ringPercent,
        right: ringPercent,
        height: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      leftEdge: {
        position: "absolute" as const,
        top: ringPercent,
        bottom: ringPercent,
        left: 0,
        width: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      rightEdge: {
        position: "absolute" as const,
        top: ringPercent,
        bottom: ringPercent,
        right: 0,
        width: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      topLeftCorner: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: ringPercent,
        height: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      topRightCorner: {
        position: "absolute" as const,
        top: 0,
        right: 0,
        width: ringPercent,
        height: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      bottomLeftCorner: {
        position: "absolute" as const,
        bottom: 0,
        left: 0,
        width: ringPercent,
        height: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
      bottomRightCorner: {
        position: "absolute" as const,
        bottom: 0,
        right: 0,
        width: ringPercent,
        height: ringPercent,
        backgroundColor: OVERLAY_COLORS.edge,
      },
    };
  }, [ringPercent, deadZones]);

  return (
    <div style={styles.container} data-testid="edge-pan-overlay">
      {/* Corners (yellow - diagonal panning) */}
      <div style={styles.topLeftCorner} />
      <div style={styles.topRightCorner} />
      <div style={styles.bottomLeftCorner} />
      <div style={styles.bottomRightCorner} />
      {/* Edges (green - cardinal panning) */}
      <div style={styles.topEdge} />
      <div style={styles.bottomEdge} />
      <div style={styles.leftEdge} />
      <div style={styles.rightEdge} />
    </div>
  );
}
